import { useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PollConstantSumForm } from "./PollConstantSumForm";
import { PollConstantSumResults } from "./PollConstantSumResults";
import { PollType, PollResultView, defaultResultViews, pollResultViewOptions } from "./pollTypes";
import { PollRankingFullForm } from "./PollRankingFullForm";
import { PollRankingMinimumForm } from "./PollRankingMinimumForm";
import { PollRankingFullResults } from "./PollRankingFullResults";
import { PollRankingMinimumResults } from "./PollRankingMinimumResults";
import { useAuth } from "@/hooks/useAuth";
import AuthModal from "@/components/AuthModal";
import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePollRendererData } from "./hooks/usePollRendererData";
import {
  applyClusterChange,
  applySubChange,
  Cluster,
  redistributeSubAllocations,
} from "./utils/allocationUtils";

interface PollRendererProps {
  postId: string;
}

export const PollRenderer = ({ postId }: PollRendererProps) => {
  const { user, isAdmin } = useAuth();
  const [hideZeroAllocated, setHideZeroAllocated] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [clusterAllocations, setClusterAllocations] = useState<Record<string, number>>({});
  const [subAllocations, setSubAllocations] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [rankingOrder, setRankingOrder] = useState<string[]>([]);
  const [minimumRequiredRanks, setMinimumRequiredRanks] = useState(3);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showExistingSubmissionDialog, setShowExistingSubmissionDialog] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const [updatingResultView, setUpdatingResultView] = useState(false);
  const clusterDragRef = useRef<{
    clusterId: string;
    baseAllocations: Record<string, number>;
  } | null>(null);
  const subDragRef = useRef<{
    subId: string;
    baseAllocations: Record<string, number>;
  } | null>(null);

  const {
    pollId,
    pollType,
    clusters,
    rankingOptions,
    resultViews,
    loading,
    submitResponse,
    setResultView,
    fetchExistingSubmission,
  } = usePollRendererData({
    postId,
    userId: user?.id,
    isAdmin,
    setClusterAllocations,
    setSubAllocations,
    setRankingOrder,
    setHideZeroAllocated,
    setMinimumRequiredRanks,
    setHasSubmitted,
    setShowResults,
    setSubmitting,
    setIsAuthModalOpen,
    setUpdatingResultView,
  });

  const handleExistingSubmissionCancel = async () => {
    await supabase.auth.signOut();
    setShowExistingSubmissionDialog(false);
    setPendingSubmit(false);
    setHasSubmitted(false);
    setShowResults(false);
  };

  const handleExistingSubmissionContinue = () => {
    setShowExistingSubmissionDialog(false);
    setShowResults(true);
  };

  const totalPoints = Object.values(clusterAllocations).reduce((sum, val) => sum + val, 0);

  const handleClusterChange = (clusterId: string, newValue: number) => {
    if (!clusters.some((cluster) => cluster.id === clusterId)) return;

    const clampedValue = Math.max(0, Math.min(100, Math.round(newValue)));
    const dragState = clusterDragRef.current;
    const baseAllocations =
      dragState && dragState.clusterId === clusterId ? dragState.baseAllocations : clusterAllocations;
    const updatedClusterAllocations = applyClusterChange(clusters, baseAllocations, clusterId, clampedValue);

    setClusterAllocations(updatedClusterAllocations);

    setSubAllocations((prev) => {
      let updated = { ...prev };
      clusters.forEach((cluster) => {
        const nextClusterValue = updatedClusterAllocations[cluster.id] || 0;
        updated = redistributeSubAllocations(cluster, nextClusterValue, updated);
      });
      return updated;
    });
  };

  const handleSubChange = (subId: string, value: number) => {
    const parentCluster = clusters.find((cluster) =>
      cluster.subCategories.some((sub) => sub.id === subId),
    );

    if (!parentCluster) {
      return;
    }

    const clusterValue = clusterAllocations[parentCluster.id] || 0;
    const clampedValue = Math.max(0, Math.min(clusterValue, Math.round(value)));
    const dragState = subDragRef.current;
    const baseAllocations = dragState && dragState.subId === subId ? dragState.baseAllocations : subAllocations;
    const updated = applySubChange(baseAllocations, subId, clampedValue, parentCluster, clusterValue);
    setSubAllocations(updated);
  };

  const handleClusterDragStart = (clusterId: string) => {
    clusterDragRef.current = {
      clusterId,
      baseAllocations: { ...clusterAllocations },
    };
  };

  const handleClusterDragEnd = () => {
    clusterDragRef.current = null;
  };

  const handleSubDragStart = (subId: string) => {
    subDragRef.current = {
      subId,
      baseAllocations: { ...subAllocations },
    };
  };

  const handleSubDragEnd = () => {
    subDragRef.current = null;
  };

  const handleSubmitClick = () => {
    if (!pollId) return;

    // Require authentication (like comments)
    if (!user) {
      setPendingSubmit(true);
      setIsAuthModalOpen(true);
      return;
    }

    // Validate before showing confirmation
    if (pollType === "ranking") {
      const orderToUse =
        rankingOrder.length === rankingOptions.length && rankingOrder.length > 0
          ? rankingOrder
          : rankingOptions.map((option) => String(option.id)).filter(Boolean);

      if (orderToUse.length !== rankingOptions.length) {
        toast.error("Please rank every option before submitting");
        return;
      }
    } else if (pollType === "ranking_minimum") {
      const orderToUse = rankingOrder.filter((id) =>
        rankingOptions.some((option) => String(option.id) === String(id)),
      );
      const required = Math.min(minimumRequiredRanks, rankingOptions.length);
      if (orderToUse.length < required) {
        toast.error(`Please rank at least ${required} option${required === 1 ? "" : "s"} before submitting`);
        return;
      }
    } else if (totalPoints !== 100) {
      toast.error("Please allocate exactly 100 points");
      return;
    }

    // Show confirmation dialog
    setShowConfirmDialog(true);
  };

  const handleConfirmedSubmit = async () => {
    setShowConfirmDialog(false);
    await submitResponse({
      clusterAllocations,
      subAllocations,
      rankingOrder,
      minimumRequiredRanks,
    });
  };

  if (loading) {
    return (
      <div id="poll-section" className="w-full my-6 border-t pt-6">
        <div className="flex justify-center py-8">
          <LoadingSpinner size={140} />
        </div>
      </div>
    );
  }

  if (
    !pollId ||
    (pollType === "constant_sum_two_level" && clusters.length === 0) ||
    ((pollType === "ranking" || pollType === "ranking_minimum") && rankingOptions.length === 0)
  ) {
    // Return empty anchor for scroll targeting even when no poll exists
    return <div id="poll-section" />;
  }

  const currentOrder = pollType === "ranking"
    ? (rankingOrder.length ? rankingOrder : rankingOptions.map((opt) => String(opt.id)).filter(Boolean))
    : pollType === "ranking_minimum"
      ? rankingOrder.filter((id) => rankingOptions.some((opt) => String(opt.id) === String(id)))
      : [];

  const isRankingType = pollType === "ranking" || pollType === "ranking_minimum";
  const requiredRanks = pollType === "ranking_minimum" ? Math.min(minimumRequiredRanks, rankingOptions.length) : rankingOptions.length;
  const activeResultView = resultViews[pollType] ?? defaultResultViews[pollType];
  const activeResultViewOptions = pollResultViewOptions[pollType] ?? [];

  return (
    <div id="poll-section" className="w-full my-6 border-t pt-6">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg font-semibold">{!showResults ? "Participate in Our Poll" : "Poll Results"}</h2>
        {showResults && isAdmin && activeResultViewOptions.length > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <Label htmlFor="result-view" className="text-muted-foreground whitespace-nowrap">
              Results view
            </Label>
            <Select
              value={activeResultView}
              onValueChange={(value) => setResultView(value as PollResultView)}
              disabled={updatingResultView}
            >
              <SelectTrigger id="result-view" className="h-9 w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {activeResultViewOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div>
                      <p className="font-medium leading-none">{option.label}</p>
                      <p className="text-xs text-muted-foreground">{option.description}</p>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      
      {!showResults ? (
        <div className="space-y-4">
          {pollType === "ranking" ? (
            <>
              <PollRankingFullForm
                options={rankingOptions}
                order={currentOrder}
                onOrderChange={setRankingOrder}
              />
              <Button
                onClick={handleSubmitClick}
                disabled={submitting || currentOrder.length < requiredRanks}
                className="w-full"
              >
                {submitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Submitting...</span>
                  </div>
                ) : (
                  "Submit Ranking"
                )}
              </Button>
            </>
          ) : pollType === "ranking_minimum" ? (
            <>
              <PollRankingMinimumForm
                options={rankingOptions}
                order={currentOrder}
                onOrderChange={setRankingOrder}
                minimumRequired={requiredRanks}
              />
              <Button
                onClick={handleSubmitClick}
                disabled={submitting || currentOrder.length < requiredRanks}
                className="w-full"
              >
                {submitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Submitting...</span>
                  </div>
                ) : (
                  "Submit Ranking"
                )}
              </Button>
            </>
          ) : (
            <>
              <PollConstantSumForm
                clusters={clusters}
                allocations={clusterAllocations}
                subAllocations={subAllocations}
                onClusterChange={handleClusterChange}
                onSubChange={handleSubChange}
                onClusterDragStart={handleClusterDragStart}
                onClusterDragEnd={handleClusterDragEnd}
                onSubDragStart={handleSubDragStart}
                onSubDragEnd={handleSubDragEnd}
                totalPoints={totalPoints}
              />
              <Button onClick={handleSubmitClick} disabled={submitting || totalPoints !== 100} className="w-full">
                {submitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Submitting...</span>
                  </div>
                ) : (
                  "Submit Response"
                )}
              </Button>
            </>
          )}
        </div>
      ) : pollType === "ranking" ? (
        <PollRankingFullResults
          pollId={pollId}
          options={rankingOptions}
          userOrder={currentOrder}
          hasSubmitted={hasSubmitted}
          viewMode={activeResultView}
        />
      ) : pollType === "ranking_minimum" ? (
        <PollRankingMinimumResults
          pollId={pollId}
          options={rankingOptions}
          userOrder={currentOrder}
          hasSubmitted={hasSubmitted}
          minimumRequiredRanks={requiredRanks}
          viewMode={activeResultView}
        />
      ) : (
        <PollConstantSumResults
          pollId={pollId}
          clusters={clusters}
          userClusterAllocations={clusterAllocations}
          userSubAllocations={subAllocations}
          hasSubmitted={hasSubmitted}
          viewMode={activeResultView}
        />
      )}

      <AuthModal 
        isOpen={isAuthModalOpen} 
        onClose={() => {
          setIsAuthModalOpen(false);
          setPendingSubmit(false);
        }}
        onSuccess={async () => {
          setIsAuthModalOpen(false);
          if (pendingSubmit) {
            setPendingSubmit(false);
            // Small delay to ensure auth state is updated
            const alreadySubmitted = pollId ? await fetchExistingSubmission(pollId) : false;

            if (alreadySubmitted) {
              setShowExistingSubmissionDialog(true);
              return;
            }

            setTimeout(() => setShowConfirmDialog(true), 100);
          }
        }}
      />

      <AlertDialog open={showExistingSubmissionDialog} onOpenChange={setShowExistingSubmissionDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Response already submitted</AlertDialogTitle>
            <AlertDialogDescription>
              You've already submitted a response on this account and you can't change your submission. Do you still
              want to log in?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleExistingSubmissionCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleExistingSubmissionContinue}>Log in anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit your response?</AlertDialogTitle>
            <AlertDialogDescription>
              Once submitted, you won't be able to change or submit another response to this poll.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmedSubmit}>Submit</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
